import { Button, Modal, Sidebar } from '@payaca/ambrosia';
import { FC, useState, type JSX } from 'react';

import InfiniteLineItemsList from '@/ui/components/infiniteLineItemsList/InfiniteLineItemsList';

type Props = {
  isOpen: boolean;
  disabledItemIds: number[];
  onClose: () => void;
  onSelectItem: (itemId: number) => void;
  /**
   * Opt-in: when provided, a "Create new Item" button is rendered below
   * the search bar. Callers wire this to their own create-Item flow.
   */
  onCreateNewItem?: () => void;
  /** When false, repeated items are blocked entirely instead of prompting. Defaults to true. */
  allowRepeatedItems?: boolean;
};
const SelectItemModal: FC<Props> = ({
  isOpen,
  disabledItemIds,
  onClose,
  onSelectItem,
  onCreateNewItem,
  allowRepeatedItems = true,
}: Props): JSX.Element => {
  const [showRepeatedItemConfirmation, setShowRepeatedItemConfirmation] =
    useState(false);
  const [showItemAlreadyAddedNotice, setShowItemAlreadyAddedNotice] =
    useState(false);
  const [itemId, setItemId] = useState<number>();

  return (
    <>
      <Sidebar
        title="Select an Item"
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
      >
        <InfiniteLineItemsList
          onLineItemClick={({ id }) => {
            if (disabledItemIds.includes(+id)) {
              if (allowRepeatedItems) {
                setItemId(+id);
                setShowRepeatedItemConfirmation(true);
              } else {
                setShowItemAlreadyAddedNotice(true);
              }
              return;
            }

            onSelectItem(+id);
          }}
          onCreateNewItem={onCreateNewItem}
        />
      </Sidebar>

      <Modal
        isOpen={showRepeatedItemConfirmation}
        title="Item already added"
        onClose={() => {
          setItemId(undefined);
          setShowRepeatedItemConfirmation(false);
        }}
        size="sm"
      >
        <Modal.Body>Item already added, want to add again?</Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="outline"
              onClick={() => {
                setItemId(undefined);
                setShowRepeatedItemConfirmation(false);
              }}
            >
              No, cancel
            </Button>
            <Button
              onClick={() => {
                onSelectItem(itemId as number);
                setItemId(undefined);
                setShowRepeatedItemConfirmation(false);
              }}
            >
              Yes, add item
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>

      <Modal
        isOpen={showItemAlreadyAddedNotice}
        title="Item already added"
        onClose={() => setShowItemAlreadyAddedNotice(false)}
        size="sm"
      >
        <Modal.Body>
          This Item has already been added and cannot be added again.
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button onClick={() => setShowItemAlreadyAddedNotice(false)}>
              OK
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SelectItemModal;
